import type { Section, SectionOverride } from './entities';

export const sectionMenus: Section[] = [
  {
    key: 'markets-news',
    label: 'Markets & News',
    shortLabel: 'Markets',
    href: '/news',
    blurb: 'Breaking headlines, the trading day, and the calendars that move it.',
    color: 'markets',
    columns: [
      {
        heading: 'News',
        links: [
          { label: 'Breaking news', href: '/news' },
          { label: 'Tech', href: '/topic/technology-stocks' },
          { label: 'Politics', href: '/general/politics' },
          { label: 'Healthcare', href: '/topic/healthcare' },
        ],
      },
      {
        heading: 'Markets',
        links: [
          { label: 'Premarket', href: '/premarket' },
          { label: 'Market update', href: '/markets' },
          { label: 'After hours', href: '/after-hours-center' },
          { label: 'Top movers', href: '/movers' },
        ],
      },
      {
        heading: 'Calendars',
        links: [
          { label: 'Earnings', href: '/calendars/earnings' },
          { label: 'Economic', href: '/calendars/economic' },
          { label: 'IPO', href: '/calendars/ipos' },
          { label: 'Dividend', href: '/dividends' },
        ],
      },
    ],
    related: {
      heading: 'Related across Benzinga',
      links: [
        { label: 'Top movers', href: '/movers', accent: 'stocks' },
        { label: "Today's rating changes", href: '/analyst-stock-ratings', accent: 'ratings' },
      ],
    },
  },
  {
    key: 'stocks-sectors',
    label: 'Stocks & Sectors',
    shortLabel: 'Stocks',
    href: '/quote',
    blurb: 'Research any ticker, then drill into sectors, ratings, and options.',
    color: 'stocks',
    columns: [
      {
        heading: 'Research a stock',
        links: [
          { label: 'Look up any quote', href: '/quote' },
          { label: 'Top movers', href: '/movers' },
          { label: 'Premarket movers', href: '/premarket' },
        ],
      },
      {
        heading: 'Sector news',
        links: [
          { label: 'Technology', href: '/topic/technology-stocks' },
          { label: 'Healthcare', href: '/topic/healthcare' },
        ],
      },
      {
        heading: 'On a quote page',
        links: [
          { label: 'Analyst ratings', href: '/analyst-stock-ratings' },
          { label: 'Insider trades', href: '/sec/insider-trades/search' },
          { label: 'Options data', href: '/markets/options' },
          { label: 'Earnings', href: '/calendars/earnings' },
        ],
      },
    ],
    related: {
      heading: 'Related across Benzinga',
      links: [
        { label: 'Stock screener', href: '/screener', accent: 'screeners' },
        { label: 'Options activity', href: '/calendars/options', accent: 'trading' },
      ],
    },
  },
  {
    key: 'ratings-research',
    label: 'Ratings & Research',
    shortLabel: 'Ratings',
    href: '/analyst-stock-ratings',
    blurb: "Wall Street calls, insider moves, and Benzinga's own stock rankings.",
    color: 'ratings',
    columns: [
      {
        heading: 'Analyst activity',
        links: [
          { label: 'Upgrades', href: '/analyst-stock-ratings/upgrades' },
          { label: 'Downgrades', href: '/analyst-stock-ratings/downgrades' },
          { label: 'Price-target changes', href: '/analyst-stock-ratings/price-target' },
          { label: 'All analyst ratings', href: '/analyst-stock-ratings' },
        ],
      },
      {
        heading: 'Ownership & flows',
        links: [
          { label: 'Insider trades (SEC)', href: '/sec/insider-trades/search' },
          { label: 'Government / Congress trades', href: '/government-trades' },
        ],
      },
      {
        heading: 'Benzinga stock rankings',
        links: [
          { label: 'Top value stocks', href: '/screener/top-value-stocks' },
          { label: 'Top growth stocks', href: '/screener/top-growth-stocks' },
          { label: 'Top quality stocks', href: '/screener/top-quality-stocks' },
          { label: 'All ranking screens', href: '/screener' },
        ],
      },
    ],
    related: {
      heading: 'Related across Benzinga',
      links: [
        { label: 'Top-ranked screen', href: '/screener/top-value-stocks', accent: 'screeners' },
        { label: 'Ratings feed', href: '/analyst-stock-ratings/upgrades', accent: 'markets' },
      ],
    },
  },
  {
    key: 'trading',
    label: 'Trading',
    shortLabel: 'Trading',
    href: '/markets/options',
    blurb: 'Options flow, trade calculators, and ideas for active traders.',
    color: 'trading',
    columns: [
      {
        heading: 'Options',
        links: [
          { label: 'Options hub', href: '/markets/options' },
          { label: 'Options activity', href: '/calendars/options' },
        ],
      },
      {
        heading: 'Tools',
        links: [
          { label: 'Options Profit Calculator', href: '/tools/options-profit-calculator' },
          { label: 'Covered Call Calculator', href: '/tools/covered-call-calculator' },
        ],
      },
      {
        heading: 'Learn to trade',
        links: [{ label: 'Pro Blog', href: '/pro/blog', note: 'Trading education & ideas' }],
      },
    ],
    related: {
      heading: 'Related across Benzinga',
      links: [
        { label: 'Options activity', href: '/calendars/options', accent: 'stocks' },
        { label: 'Try Benzinga Pro', href: '/pro', accent: 'brand' },
      ],
    },
  },
  {
    key: 'screeners-lists',
    label: 'Screeners & Lists',
    shortLabel: 'Screeners',
    href: '/screener',
    blurb: 'Filter the market your way, or start from a curated list.',
    color: 'screeners',
    columns: [
      {
        heading: 'Screeners',
        links: [
          { label: 'Stock & ETF screener', href: '/screener' },
          { label: 'Saved screens', href: '/screener' },
        ],
      },
      {
        heading: 'Popular lists',
        links: [
          { label: 'Best dividend stocks', href: '/money/best-dividend-stocks-to-buy-and-hold' },
          { label: 'Best growth stocks', href: '/money/best-growth-stocks' },
          { label: 'Best AI stocks', href: '/money/best-artificial-intelligence-stocks' },
          { label: 'Top stocks under $5', href: '/money/top-stocks-under-5' },
        ],
      },
      {
        heading: 'Rankings by strategy',
        links: [
          { label: 'Value', href: '/screener/top-value-stocks' },
          { label: 'Growth', href: '/screener/top-growth-stocks' },
          { label: 'Momentum', href: '/screener/top-momentum-stocks' },
          { label: 'Quality', href: '/screener/top-quality-stocks' },
        ],
      },
    ],
    related: {
      heading: 'Related across Benzinga',
      links: [
        { label: 'Live market news', href: '/news', accent: 'markets' },
        { label: 'How to invest', href: '/money/how-to-invest-money', accent: 'money' },
      ],
    },
  },
  {
    key: 'personal-finance',
    label: 'Personal Finance',
    shortLabel: 'Finance',
    href: '/money',
    blurb: "Pick a broker, grow your savings, and protect what you've built.",
    color: 'money',
    columns: [
      {
        heading: 'Invest',
        links: [
          { label: 'Best brokers', href: '/money/browse-brokers' },
          { label: 'Best investing apps', href: '/money/best-investment-apps' },
          { label: 'How to invest', href: '/money/how-to-invest-money' },
        ],
      },
      {
        heading: 'Save & borrow',
        links: [
          { label: 'Best savings accounts', href: '/money/best-savings-accounts-in-the-united-states' },
          { label: 'Best mortgage lenders', href: '/money/best-mortgage-lenders' },
        ],
      },
      {
        heading: 'Protect & grow',
        links: [
          { label: 'Life insurance', href: '/money/best-life-insurance' },
          { label: 'Crypto exchanges', href: '/money/best-crypto-exchanges' },
        ],
      },
    ],
    related: {
      heading: 'Related across Benzinga',
      links: [
        { label: 'Top value stocks', href: '/screener/top-value-stocks', accent: 'screeners' },
        { label: 'Live market news', href: '/news', accent: 'markets' },
      ],
    },
  },
];

export const getSectionMenus = (override?: SectionOverride): Section[] => {
  if (!override?.length) {
    return sectionMenus;
  }
  const overridesByKey = new Map(override.map(entry => [entry.key, entry]));
  return sectionMenus.map(section => {
    const entry = overridesByKey.get(section.key);
    return entry ? { ...section, ...entry, key: section.key } : section;
  });
};
